import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, filter, map, of, switchMap, take, tap} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {environment} from '@env/environment';
import * as FlowActions from './flow.actions';
import {FlowApiService} from '../services/flow-api.service';
import {TranslocoService} from '@jsverse/transloco';
import {App as CapacitorApp} from '@capacitor/app';
import {Capacitor} from '@capacitor/core';
import {Browser} from '@capacitor/browser';
import {TabVisibilityService} from '@components/footer/tab-visibility.service';

@Injectable()
export class FlowEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _flowApiService = inject(FlowApiService);
  private readonly _translocoService = inject(TranslocoService);
  private readonly _tabVisibility = inject(TabVisibilityService);

  checkAppVersion$ = createEffect(() =>
    this._actions$.pipe(
      ofType(FlowActions.checkAppVersion),
      switchMap(() => {
        const normalize = (v: string) => v.replace(/^v/, '').replace(/\.0+$/, '');
        const appVersion = normalize(environment.version);


        return this._flowApiService.getAvailableVersions().pipe(
          map((versions) => {

            const available = versions.map(v => v.name);
            const currentEntry = versions.find(v => normalize(v.name) === appVersion);

            if (!currentEntry) {
              return FlowActions.checkAppVersionSuccess({
                status: 'invalid',
                current: appVersion,
                available
              });
            }

            if (currentEntry.state === 'obsolete') {
              return FlowActions.checkAppVersionSuccess({
                status: 'obsolete',
                expiresAt: currentEntry.final_validity_date,
                current: appVersion,
                available
              });
            }

            return FlowActions.checkAppVersionSuccess({
              status: 'valid',
              current: appVersion,
              available
            });
          }),
          catchError((error) => {
            console.error('[FlowEffects] Error checking version:', error);
            return of(FlowActions.checkAppVersionSuccess({
              status: 'valid',
              current: normalize(environment.version),
              available: []
            }));
          })
        );
      })
    )
  );

  notifyIfObsolete$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(FlowActions.checkAppVersionSuccess),
        // mostra notifica solo se NON è web
        filter(() => Capacitor.getPlatform() !== 'web'),
        tap(async ({status, expiresAt}) => {
          const platform = Capacitor.getPlatform();
          const storeLink =
            platform === 'ios'
              ? environment.iosLink
              : platform === 'android'
                ? environment.androidLink
                : null;

          if (status === 'invalid') {
            this._translocoService.selectTranslate('version.invalid')
              .pipe(take(1))
              .subscribe(async (msg) => {
                alert(msg);
                if (storeLink) await Browser.open({url: storeLink});
                if (Capacitor.isNativePlatform()) await CapacitorApp.exitApp();
              });

          } else if (status === 'obsolete') {
            this._translocoService.selectTranslate('version.obsolete', {expiresAt})
              .pipe(take(1))
              .subscribe(async (msg) => {
                alert(msg);
                if (storeLink) await Browser.open({url: storeLink});
              });
          }
        })
      );
    },
    {dispatch: false}
  );

  showVersion$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(FlowActions.showVersion), tap(() => {
          this._snackBar.open(`Version: ${environment.version}`, '', {
            duration: 3000,
            panelClass: 'snackbar'
          });
        })
      ),
    {dispatch: false}
  );

  saveOnboarding$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(FlowActions.setHasSeenOnboarding),
        tap(({value}) => {
          localStorage.setItem('hasSeenOnboarding', String(value));
        })
      ),
    {dispatch: false}
  );

  setGuest = createEffect(() => this._actions$.pipe(ofType(FlowActions.setGuest), tap(({value}) => {
      localStorage.setItem('guest', String(value));
    }),
    tap(({value}) => {
      this._tabVisibility.setGuestMode(value);
    })), {dispatch: false})
}
